import { getSelectorData, getSelectorStringData } from 'helpers/services/services.utils';

// Module-level promise cache for `getSelectorData` / `getSelectorStringData`
// lookups, mirroring `getDataCache.ts`. Mounting many List fields in a
// detail grid (one per row), or running calculate over many rows that
// reference the same string-list editor, would otherwise fire the same
// EDITOR request again and again; this dedupes them.
// Cache is cleared on successful Save / Finish so the next document
// starts fresh.

type GetSelectorDataParams = {
  editor: string | number;
};

const cache = new Map<string, Promise<any>>();
const stringCache = new Map<string, Promise<any>>();

function buildKey({ editor }: GetSelectorDataParams) {
  return `${editor ?? ''}`;
}

export async function cachedGetSelectorData(params: GetSelectorDataParams) {
  const cacheKey = buildKey(params);

  let pending = cache.get(cacheKey);
  if (!pending) {
    pending = (async () => {
      try {
        const result = await getSelectorData({
          queryKey: ['getSelectorData', { editor: params.editor }],
        });
        return result;
      } catch (error) {
        cache.delete(cacheKey);
        throw error;
      }
    })();
    cache.set(cacheKey, pending);
  }

  return pending;
}

export async function cachedGetSelectorStringData(params: GetSelectorDataParams) {
  const cacheKey = buildKey(params);

  let pending = stringCache.get(cacheKey);
  if (!pending) {
    pending = (async () => {
      try {
        const result = await getSelectorStringData({
          queryKey: ['getSelectorStringData', { editor: params.editor }],
        });
        return result;
      } catch (error) {
        stringCache.delete(cacheKey);
        throw error;
      }
    })();
    stringCache.set(cacheKey, pending);
  }

  return pending;
}

export function clearGetSelectorDataCache() {
  cache.clear();
  stringCache.clear();
}
